import { Component, DestroyRef, inject } from "@angular/core";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { filter, tap } from "rxjs";
import { isUndefined, isNullOrUndefined } from "src/app/helpers/utils";
import { DataService } from "src/app/services/data.service";
import { FormService } from "src/app/services/form.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "formly-field-ng-select",
  template: `
    <ng-container *ngIf="areReadOnly; else selectFieldTemplate">
      <div *ngIf="props.label" [class]="props['labelClass']">{{ props.label }}</div>
      <div [class]="props['fieldClass']">
        {{ formControl.value || " - " }}
      </div>
    </ng-container>
    <ng-template #selectFieldTemplate>
      <label [class]="props['labelClass']" [for]="key" *ngIf="props.label"
        >{{ props.label }}
        <span
          *ngIf="props.required"
        >
          *</span
        >
      </label>
      <div *ngIf="props['subLabelTitle']" [ngClass]="props['subLabelClass']">
        {{ props['subLabelTitle'] }}
      </div>
      <ng-select
        [items]="options$"
        [bindLabel]="labelProp"
        [bindValue]="valueProp"
        [groupBy]="groupProp"
        [multiple]="props['multiple']"
        [closeOnSelect]="true"
        [clearable]="props['clearable']"
        placeholder="{{ props.placeholder }}"
        [labelForId]="key"
        [formControl]="formControl"
        (change)="onChangeValue($event)"
        [required]="props.required ?? false"
      >
      </ng-select>
      <div *ngIf="showError" class="col-3 invalid-feedback d-block ngselect-error">
        <formly-validation-message [field]="field"></formly-validation-message>
      </div>
    </ng-template>
  `,
})
export class FormlyFieldNgSelect extends FieldType<FieldTypeConfig> {
  private destroyRef = inject(DestroyRef);

  options$?: any;

  loaded?: boolean;

  areReadOnly: boolean = false;

  constructor(
    private ds: DataService,
    private formService: FormService
  ) {
    super();
  }

  onChangeValue(event: any) {
    this.ds.setModelData(this.model);
  }

  ngOnInit() {
    this.areReadOnly = this.props["readOnly"];
    this.options$ = this.props.options; // | async
    this.loaded = true;

    if (!isNullOrUndefined(this.props["dependOn"]) && !isNullOrUndefined(this.props["endpoint"])) {
      this.ds.currentData$.subscribe((Obj) => {
        if (Obj?.id && Obj?.structureId === this.props["dependOn"]) {
          this.ds.getData(this.props["endpoint"] + "/" + Obj.id).subscribe((data) => {
            this.options$ = data.data || data[this.props["extract"]] || data;
            this.formControl.setValue(null);
          });
        } else {
          if (Array.isArray(this.props["otherDep"]) && this.props["otherDep"].includes(Obj?.structureId)) {
            this.formControl.setValue(null);
            this.options$ = [];
          }
        }
      });
    }

    if (!isNullOrUndefined(this.props["endpoint"]) && isNullOrUndefined(this.props["dependOn"])) {
      this.setOptions(this.model);
    }

    this.formControl.statusChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: string) => event === "DISABLED"),
        tap(() => {
          this.formControl.reset();
        })
      )
      .subscribe();

    this.formControl.valueChanges.subscribe((change) => {
      if (this.options$) {
        let resource = this.options$.find((element: any) => element.id === change);
        if (resource) {
          resource.structureId = this.key;
          this.ds.setData(resource);
        }
      }
    });
  }

  setOptions(model: any = null) {
    const getData = (endpoint: string) => {
      this.ds.getData(endpoint).subscribe((dataList) => {
        dataList = this.props["extract"] ? dataList[this.props["extract"]] : dataList;
        let endpointObjectsToFilter = this.props["removeSelected"];
        if (endpointObjectsToFilter) {
          endpointObjectsToFilter = ((model && model.id) && this.props["removeSelectedId"]) ? (endpointObjectsToFilter + "/" + model.id) : endpointObjectsToFilter;
          this.ds.getData(endpointObjectsToFilter).subscribe((data1) => {
            this.options$ = dataList.filter((t: any) => !data1.map((f: any) => f.id).includes(t.id));
          });
        } else {
          this.options$ = dataList;
        }
        if (this.props["setAllValue"]) {
          this.setAllValue();
        } else this.setValue();
      });
    };
    this.checkEndpoint(getData);
  }

  checkEndpoint(getDataCallBack: Function) {
    let endpoint = this.props["endpoint"];
    this.ds.modelData$.subscribe((modelResource) => {
      const regex = /\{([a-zA-Z]*)\}/i;
      const par = endpoint.match(regex);
      if (modelResource) {
        if (endpoint.match("{")) {
          if (par && modelResource[par[1]]) {
            endpoint = endpoint.replace(par[0], "/" + modelResource[par[1]]);
            getDataCallBack(endpoint);
          }
        } else {
          getDataCallBack(endpoint);
        }
      } else if (!this.props["skipCheck"] && (this.props["removeSelected"] || !this.model.id)) {
        if (endpoint.match("{")) {
          endpoint = endpoint.replace(par[0], "");
        }
        getDataCallBack(endpoint);
      }
      if (!modelResource && endpoint) {
        getDataCallBack(endpoint);
      }
    });
  }

  setAllValue() {
    this.formControl.setValue(this.options$.map((entry: any) => entry.id));
  }

  setValue() {
    this.ds.modelData$.subscribe((modelResource) => {
      if (modelResource) {
        let key: any = this.snakeToCamelCase(this.key);
        this.formControl.setValue(modelResource[key]);
        this.loaded = true;
      }
    });
  }

  camelToSnakeCase(str: any) {
    return str.replace(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`);
  }
  snakeToCamelCase(str: any) {
    return str.replace(/[-_][a-z]/gi, (letter: string) => `${letter.toUpperCase()}`).replace("_", "");
  }

  onChange(event: any) {
    if (event) {
      event.structureId = this.key;
      this.ds.setData(event);
    }
  }

  getTo() {
    let options = this.options;
    return isUndefined(options) ? [] : (options as any[]);
  }

  get multiple(): boolean {
    return this.props["multiple"];
  }
  get labelProp(): string {
    return this.props["labelProp"] || "label";
  }
  get valueProp(): string {
    return this.props["valueProp"] || "value";
  }
  get groupProp(): string {
    return this.props["groupProp"] || "group";
  }
}
